import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import InputField from '@/components/common/InputField';
import { toastMessage } from '@/utils/toast-message';
import { setReminder } from '@/actions/fetch-leads';
import { addEvent, updateEvent } from '@/actions/data-fetch-activities';
import { DeleteEventp } from '@/actions/data-fetch-activities';
import { Calendar, Clock, Bell, Sparkles, Trash2, Save, Plus } from 'lucide-react';

export interface Event {
    event_title: string;
    db_start: string;
    alert_before: string;
    uuid?: string;
}

// Define alert time options
const ALERT_TIME_OPTIONS = [
    { value: '15 min', label: '15 minutes before' },
    { value: '30 min', label: '30 minutes before' },
    { value: '1 hour', label: '1 hour before' },
    { value: '2 hours', label: '2 hours before' },
    { value: '1 day', label: '1 day before' },
    { value: '2 days', label: '2 days before' }
];

// Define Zod validation schema
const reminderSchema = z.object({
    event_title: z.string().min(3, 'Title must be at least 3 characters').max(100, 'Title must not exceed 100 characters'),
    start_date: z.string().min(1, 'Date and time is required'),
    alert_before: z.enum(['15 min', '30 min', '1 hour', '2 hours', '1 day', '2 days'], {
        errorMap: () => ({ message: 'Please select an alert time' })
    }).optional(),
});

// Type inference from schema
export type ReminderFormData = z.infer<typeof reminderSchema>;

interface ReminderFormProps {
    leadUuid?: string | null;
    selectedItems?: Array<{ uuid: string;[key: string]: any }>;
    selectedItemsActivities?: Event[];
    onCancel?: () => void;
    refreshData?: () => void;
    onClose?: () => void;
    showDelete: boolean;
    isActivity: boolean;
    onDelete?: (value: boolean) => void;
}

const ReminderForm: React.FC<ReminderFormProps> = ({
    leadUuid,
    selectedItems,
    selectedItemsActivities,
    showDelete,
    onCancel,
    refreshData,
    isActivity,
    onClose,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventuuid, seteventuuid] = useState<string>();
    const [isEditMode, setIsEditMode] = useState(false); // Track if we're editing

    console.log("selectedItemsActivities", selectedItemsActivities);

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<ReminderFormData>({
        resolver: zodResolver(reminderSchema),
        defaultValues: {
            event_title: '',
            start_date: '',
            alert_before: '15 min',
        },
    });

    useEffect(() => {
        if (selectedItemsActivities && selectedItemsActivities.length > 0) {
            // console.log("selectedItemsActivities", selectedItemsActivities);
            const selectedEvent = selectedItemsActivities[0];

            // Check if this is an existing event (has uuid and title)
            const isExistingEvent = !!(selectedEvent.uuid && selectedEvent.event_title);
            setIsEditMode(isExistingEvent);

            setValue('event_title', selectedEvent.event_title);

            // Convert db_start format from "YYYY-MM-DD HH:mm:ss" to "YYYY-MM-DDTHH:mm" for datetime-local input
            let formattedDate = selectedEvent.db_start;
            if (formattedDate && formattedDate.includes(' ')) {
                // If format is "YYYY-MM-DD HH:mm:ss", convert to "YYYY-MM-DDTHH:mm"
                formattedDate = formattedDate.slice(0, 16).replace(' ', 'T');
            }
            setValue('start_date', formattedDate);

            setValue(
                'alert_before',
                (selectedEvent.alert_before as ReminderFormData['alert_before']) || '15 min'
            );
            seteventuuid(selectedEvent.uuid);
        }
    }, [selectedItemsActivities, setValue]);

    const deleteEvent = async () => {
        if (!eventuuid) {
            toastMessage('No event selected for deletion', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            await DeleteEventp(eventuuid);

            toastMessage('Event deleted successfully!', 'success');

            if (refreshData) {
                refreshData();
            }

            if (onClose) {
                onClose();
            }
        } catch (err) {
            console.log("Error deleting event:", err);
            toastMessage('Failed to delete event', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormSubmit = async (formData: ReminderFormData) => {
        setIsSubmitting(true);

        let uuids: string[] = [];

        if (selectedItems && selectedItems.length > 0) {
            uuids = selectedItems.map(item => item.uuid);
        } else if (leadUuid) {
            uuids = [leadUuid];
        }

        if (uuids.length === 0) {
            toastMessage('No items selected. Cannot set reminder.', 'error');
            setIsSubmitting(false);
            return;
        }

        try {
            const alertBefore = formData.alert_before || '15 min';

            const response = await setReminder({
                params: {
                    leadUuid: uuids,
                    event_title: formData.event_title,
                    daterange: formData.start_date,
                    alert_before: alertBefore
                }
            });

            const dataRes = response.data as any;
            toastMessage(dataRes?.message || 'Reminder set successfully!', 'success');

            reset();

            if (refreshData) {
                refreshData();
            }

            if (onClose) {
                onClose();
            }
        } catch (error) {
            let errorMessage: string;
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else {
                errorMessage = 'Setting reminder failed';
            }

            toastMessage(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleActivitySubmit = async (formData: ReminderFormData) => {
        setIsSubmitting(true);

        try {
            const alertBefore = formData.alert_before || '15 min';
            const params = {
                event_title: formData.event_title,
                db_start: formData.start_date,
                alert_before: alertBefore,
            };

            let response;

            if (isEditMode && eventuuid) {
                // Update existing event
                response = await updateEvent(eventuuid, params);
                toastMessage(response.message || 'Event updated successfully!', 'success');
            } else {
                // Create new event
                response = await addEvent(params);
                toastMessage(response.message || 'Event created successfully!', 'success');
            }

            console.log('Response:', response);

            reset();

            if (refreshData) {
                refreshData();
            }

            // Close modal on success
            if (onClose) {
                onClose();
            }

        } catch (err) {
            console.log("Error processing event:", err);
            const errorMessage = isEditMode ? 'Failed to update event' : 'Failed to create event';
            toastMessage(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSubmitHandler = (formData: ReminderFormData) => {
        if (isActivity) {
            return handleActivitySubmit(formData);
        } else {
            return handleFormSubmit(formData);
        }
    };

    return (
        <div className="bg-white">
            {/* Header with solid brand color */}
            <div style={{ backgroundColor: import.meta.env.VITE_BRAND_COLOR }} className="p-4">
                <div className="flex items-center space-x-2 text-white">
                    <div className="bg-white/20 p-2 rounded-md">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">
                            {isEditMode ? 'Edit Event' : 'Create New Event'}
                        </h3>
                        <p className="text-white/90 text-xs">
                            {isEditMode ? 'Update your event details' : 'Schedule a new event or reminder'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmitHandler)} className="p-4 space-y-4">
                {/* Event Title Field */}
                <div className="space-y-1">
                    <label className="flex items-center text-sm font-medium text-gray-700">
                        <Sparkles className="w-4 h-4 mr-1.5" style={{ color: import.meta.env.VITE_BRAND_COLOR }} />
                        Event Title
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <InputField
                        id="event_title"
                        name="event_title"
                        type="text"
                        placeholder="e.g., Team Meeting, Client Call, Project Deadline..."
                        control={control}
                        error={!!errors.event_title?.message}
                        required
                    />
                    {errors.event_title && (
                        <p className="text-xs text-red-600">{errors.event_title.message}</p>
                    )}
                </div>

                {/* Date & Time Field */}
                <div className="space-y-1">
                    <label className="flex items-center text-sm font-medium text-gray-700">
                        <Clock className="w-4 h-4 mr-1.5" style={{ color: import.meta.env.VITE_PRIMARY_BLUE }} />
                        Start Date & Time
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <InputField
                        id="start_date"
                        name="start_date"
                        type="datetime-local"
                        control={control}
                        error={!!errors.start_date?.message}
                        required
                        min={new Date().toISOString().slice(0, 16)}
                    />
                    {errors.start_date && (
                        <p className="text-xs text-red-600">{errors.start_date.message}</p>
                    )}
                </div>

                {/* Alert Before Field */}
                <div className="space-y-1">
                    <label className="flex items-center text-sm font-medium text-gray-700">
                        <Bell className="w-4 h-4 mr-1.5" style={{ color: import.meta.env.VITE_WARNING_AMBER }} />
                        Alert Before
                    </label>
                    <InputField
                        id="alert_before"
                        name="alert_before"
                        type="select"
                        options={ALERT_TIME_OPTIONS}
                        control={control}
                        error={!!errors.alert_before?.message}
                    />
                    <p className="text-xs text-gray-500">
                        You'll receive a notification before the event starts
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <div>
                        {showDelete && eventuuid && (
                            <button
                                type="button"
                                onClick={deleteEvent}
                                disabled={isSubmitting}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <Trash2 className="w-4 h-4 mr-1.5" />
                                {isSubmitting ? 'Deleting...' : 'Delete Event'}
                            </button>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={isSubmitting}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                style={{ '--tw-ring-color': import.meta.env.VITE_BRAND_COLOR } as React.CSSProperties}
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            style={{
                                backgroundColor: import.meta.env.VITE_BRAND_COLOR,
                                '--tw-ring-color': import.meta.env.VITE_BRAND_COLOR
                            } as React.CSSProperties}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = import.meta.env.VITE_BRAND_COLOR_HOVER}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = import.meta.env.VITE_BRAND_COLOR}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {isEditMode ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    {isEditMode ? <Save className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                                    {isEditMode ? 'Update Event' : 'Create Event'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ReminderForm;